import React, { useState, useEffect } from 'react';
import { FolderOpen, X, ChevronDown, ChevronUp, Monitor, Globe, Download, Bug } from 'lucide-react';
import { databasesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  setDownloadLocation,
  getLocationLabel,
  clearDownloadLocation,
  isFileSystemAccessSupported,
} from '../../utils/downloadLocation';
import { isDebugMode, setDebugMode } from '../../utils/debug';

const ZOOM_OPTIONS = ['75%', '80%', '90%', '100%', '110%', '125%', '150%'];
const CONCURRENCY_OPTIONS = [
  { label: '1',         value: '1' },
  { label: '2',         value: '2' },
  { label: '3',         value: '3' },
  { label: 'Unlimited', value: 'unlimited' },
];
const LOCATION_KEYS = [
  { key: 'app-default',    label: 'Application Default',   sub: 'Fallback for all downloads when no specific location is set' },
  { key: 'local-reports',  label: 'Local Report Exports',  sub: 'PDF, Excel, and CSV reports generated on this machine' },
  { key: 'remote-reports', label: 'Remote Re-downloads',   sub: 'Reports re-downloaded from another machine\'s history' },
];

// ── Session detection ─────────────────────────────────────────────────────────
const detectSession = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'PC';

  if      (ua.includes('Edg/'))                          browser = 'Microsoft Edge';
  else if (ua.includes('Chrome/') && !ua.includes('OPR')) browser = 'Chrome';
  else if (ua.includes('Firefox/'))                       browser = 'Firefox';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';

  if      (ua.includes('Windows NT'))  os = 'Windows';
  else if (ua.includes('Mac OS X'))    os = 'macOS';
  else if (ua.includes('Android'))     os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux'))       os = 'Linux';

  if      (ua.includes('Mobile'))  device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad')) device = 'Tablet';

  return { browser, os, device };
};

// ── Collapsible section ───────────────────────────────────────────────────────
const PrefSection = ({ id, title, icon: Icon, defaultOpen = true, children }) => {
  const key = `pmd-pref-section-${id}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved !== null ? saved === 'true' : defaultOpen;
  });

  const toggle = () => {
    const next = !open;
    localStorage.setItem(key, next.toString());
    setOpen(next);
  };

  return (
    <div className="pref-card">
      <div className="pref-card-header" onClick={toggle}>
        <div className="pref-card-header-left">
          {Icon && <Icon size={15} style={{ color: 'var(--color-primary)' }} />}
          <span className="pref-section-title" style={{ margin: 0 }}>{title}</span>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {open && <div className="pref-card-body">{children}</div>}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const PreferencesPage = () => {
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();
  const [databases, setDatabases]   = useState([]);
  const [defaultDb, setDefaultDb]   = useState(localStorage.getItem('pmd-selected-db-slug') || '');
  const [zoom, setZoomState]        = useState(localStorage.getItem('dawn.uiZoom') || '100%');
  const [concurrency, setConcurrencyState] = useState(localStorage.getItem('activityTracker.concurrencyLimit') || '2');
  const [debugMode, setDebugState]  = useState(isDebugMode());
  const [locationLabels, setLocationLabels] = useState({ 'app-default': null, 'local-reports': null, 'remote-reports': null });

  const fsSupported = isFileSystemAccessSupported();
  const session     = detectSession();

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadLabels = async () => {
      const [appDef, local, remote] = await Promise.all([
        getLocationLabel('app-default'),
        getLocationLabel('local-reports'),
        getLocationLabel('remote-reports'),
      ]);
      setLocationLabels({ 'app-default': appDef, 'local-reports': local, 'remote-reports': remote });
    };
    loadLabels();
  }, []);

  const handleZoom = (value) => {
    setZoomState(value);
    localStorage.setItem('dawn.uiZoom', value);
    document.documentElement.style.zoom = value === '100%' ? '' : value;
  };

  const handleConcurrency = (value) => {
    setConcurrencyState(value);
    localStorage.setItem('activityTracker.concurrencyLimit', value);
  };

  const handleDefaultDb = (slug) => {
    setDefaultDb(slug);
    localStorage.setItem('pmd-selected-db-slug', slug);
    const db = databases.find(d => (d.displayName || d.name)?.toLowerCase() === slug);
    if (db) localStorage.setItem('pmd-selected-database', db.databaseName || db.id);
  };

  const handleDebugToggle = (e) => {
    setDebugState(e.target.checked);
    setDebugMode(e.target.checked);
  };

  const handleBrowse = async (key) => {
    const label = await setDownloadLocation(key);
    if (label) setLocationLabels(prev => ({ ...prev, [key]: label }));
  };

  const handleClearLocation = async (key) => {
    await clearDownloadLocation(key);
    setLocationLabels(prev => ({ ...prev, [key]: null }));
  };

  return (
    <div className="preferences-page">
      <div className="preferences-title">Preferences</div>

      {/* ── This Session ── */}
      <div className="pref-card pref-session-card">
        <div className="pref-card-header" style={{ cursor: 'default' }}>
          <div className="pref-card-header-left">
            <Monitor size={15} style={{ color: 'var(--color-primary)' }} />
            <span className="pref-section-title" style={{ margin: 0 }}>This Session</span>
          </div>
        </div>
        <div className="pref-card-body">
          <div className="pref-session-grid">
            <div className="pref-session-item">
              <span className="pref-sublabel">Browser</span>
              <span className="pref-label">{session.browser}</span>
            </div>
            <div className="pref-session-item">
              <span className="pref-sublabel">Operating System</span>
              <span className="pref-label">{session.os}</span>
            </div>
            <div className="pref-session-item">
              <span className="pref-sublabel">Device</span>
              <span className="pref-label">{session.device}</span>
            </div>
            <div className="pref-session-item">
              <span className="pref-sublabel">User</span>
              <span className="pref-label">{currentUser?.displayName || currentUser?.email || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── General ── */}
      <PrefSection id="general" title="General" icon={Globe}>
        <div className="pref-row">
          <div>
            <div className="pref-label">Default Database</div>
            <div className="pref-sublabel">Database to open on login</div>
          </div>
          <select
            value={defaultDb}
            onChange={e => handleDefaultDb(e.target.value)}
            style={{
              fontSize: '12px', padding: '5px 10px', borderRadius: '6px',
              border: '1px solid var(--color-gray-300)', background: '#fff',
              color: 'var(--color-gray-700)', cursor: 'pointer',
            }}
          >
            {databases.map(db => {
              const name = db.displayName || db.name;
              const slug = name?.toLowerCase();
              return <option key={db.id} value={slug}>{name}</option>;
            })}
          </select>
        </div>
      </PrefSection>

      {/* ── Display ── */}
      <PrefSection id="display" title="Display" icon={Monitor}>
        <div className="pref-row">
          <div>
            <div className="pref-label">UI Zoom Level</div>
            <div className="pref-sublabel">Scales the DAWN interface (this machine only)</div>
          </div>
          <div className="pref-pill-group">
            {ZOOM_OPTIONS.map(z => (
              <button key={z} className={`pref-pill${zoom === z ? ' selected' : ''}`} onClick={() => handleZoom(z)}>
                {z}
              </button>
            ))}
          </div>
        </div>
        <div className="pref-note">Zoom affects the DAWN application only, not your browser chrome or other tabs.</div>
      </PrefSection>

      {/* ── Downloads ── */}
      <PrefSection id="downloads" title="Downloads" icon={Download}>
        <div className="pref-row">
          <div>
            <div className="pref-label">Max Concurrent Downloads</div>
            <div className="pref-sublabel">Additional downloads queue until a slot opens</div>
          </div>
          <div className="pref-pill-group">
            {CONCURRENCY_OPTIONS.map(opt => (
              <button key={opt.value} className={`pref-pill${concurrency === opt.value ? ' selected' : ''}`} onClick={() => handleConcurrency(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pref-section-title" style={{ marginTop: '20px', marginBottom: '12px' }}>Download Locations</div>

        {!fsSupported && (
          <div className="pref-note" style={{ marginBottom: '12px', color: '#854d0e', background: '#fef9c3', padding: '8px 12px', borderRadius: '6px', fontStyle: 'normal' }}>
            Custom download locations require Chrome or Edge. Other browsers will always show the system save dialog.
          </div>
        )}

        {LOCATION_KEYS.map(({ key, label, sub }) => (
          <div key={key} className="pref-row" style={{ alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ minWidth: 220 }}>
              <div className="pref-label">{label}</div>
              <div className="pref-sublabel">{sub}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {locationLabels[key] ? (
                <>
                  <span className="pref-location-display">{locationLabels[key]}</span>
                  <button className="pref-browse-btn" onClick={() => handleBrowse(key)} disabled={!fsSupported}>
                    <FolderOpen size={13} style={{ marginRight: 4 }} /> Change
                  </button>
                  <button className="pref-clear-btn" onClick={() => handleClearLocation(key)} title="Clear stored location">
                    <X size={12} />
                  </button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '11px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
                    {key === 'app-default' ? 'System default (browser downloads)' : 'Use Application Default'}
                  </span>
                  <button className="pref-browse-btn" onClick={() => handleBrowse(key)} disabled={!fsSupported}>
                    <FolderOpen size={13} style={{ marginRight: 4 }} /> Browse...
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </PrefSection>

      {/* ── Debug ── */}
      <PrefSection id="debug" title="Debug" icon={Bug} defaultOpen={false}>
        <div className="pref-row">
          <div>
            <div className="pref-label">Enable Debug Logging</div>
            <div className="pref-sublabel">Verbose console output for activity tracking, exports, and download resolution</div>
          </div>
          <label className="pref-toggle">
            <input type="checkbox" checked={debugMode} onChange={handleDebugToggle} />
            <span className="pref-toggle-slider" />
          </label>
        </div>
        {debugMode && (
          <div className="pref-note" style={{ color: '#854d0e' }}>
            Debug mode is active — open the browser console to see verbose logs.
          </div>
        )}
      </PrefSection>
    </div>
  );
};

export default PreferencesPage;
